import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientFeedbackPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    source: '',
    quality: '',
    weak_points: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/feedback`, formData);
      toast.success(
        language === 'en' 
          ? 'Thank you for your feedback!' 
          : 'Merci pour votre retour!'
      );
      setFormData({
        source: '',
        quality: '',
        weak_points: ''
      });
    } catch (error) {
      toast.error(
        language === 'en'
          ? 'Failed to send. Please try again.'
          : 'Échec de l\'envoi. Veuillez réessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {language === 'en' ? 'Your Opinion' : 'Votre Avis'}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {t('clientFeedback.title')}
          </h1>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Question 1 */}
              <div className="bg-white rounded-2xl p-8">
                <Label className="text-lg font-medium text-slate-800 mb-6 block">
                  {t('clientFeedback.questions.q1')}
                </Label>
                <RadioGroup
                  value={formData.source}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, source: value }))}
                  className="space-y-3"
                >
                  {t('clientFeedback.questions.q1Options').map((option, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <RadioGroupItem value={option} id={`source-${index}`} />
                      <Label htmlFor={`source-${index}`} className="text-slate-600 cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Question 2 */}
              <div className="bg-white rounded-2xl p-8">
                <Label className="text-lg font-medium text-slate-800 mb-6 block">
                  {t('clientFeedback.questions.q2')}
                </Label>
                <RadioGroup
                  value={formData.quality}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, quality: value }))}
                  className="space-y-3"
                >
                  {t('clientFeedback.questions.q2Options').map((option, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <RadioGroupItem value={option} id={`quality-${index}`} />
                      <Label htmlFor={`quality-${index}`} className="text-slate-600 cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Question 3 (conditional) */}
              {formData.quality === (language === 'en' ? 'Poor' : 'Mauvaise') && (
                <div className="bg-white rounded-2xl p-8">
                  <Label className="text-lg font-medium text-slate-800 mb-6 block">
                    {t('clientFeedback.questions.q3')}
                  </Label>
                  <Textarea
                    value={formData.weak_points}
                    onChange={(e) => setFormData(prev => ({ ...prev, weak_points: e.target.value }))}
                    rows={4}
                    className="border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>
              )}

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-slate-800 hover:bg-slate-700 text-white h-12 rounded-full"
              >
                {loading 
                  ? (language === 'en' ? 'Sending...' : 'Envoi en cours...')
                  : t('clientFeedback.submit')
                }
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ClientFeedbackPage;
